<template>
  <span
    v-if="isPlain"
    class="ForumResponseBodyTextOutputMention"
    :class="classList"
    :prospectId="prospectId"
    :userId="userId"
    :fullName="fullName"
    :isAnonymous="isAnonymous"
    :toModerator="toModerator"
    :isReply="isReply"
    data-do-not-highlight
  >
    {{ text }}
  </span>
  <LLPopper
    v-else
    class="ForumResponseBodyTextOutputMention"
    :class="classList"
    :offset="[0, 5]"
    placement="bottom-start"
    tooltip-white
    tooltip-with-arrow
  >
    <template #trigger>{{ text }}</template>
    <template #tooltip>
      <MemberProfileCard v-if="hasAnonymousTarget" :anonymous-id="anonymousId" />
      <MemberProfileCard v-else-if="hasMemberTarget" :chat-id="chatId" :member-id="prospectId" />
      <MemberProfileCard v-else-if="hasUserTarget" :chat-id="chatId" :member-id="userId" />
      <MemberProfileCard v-else moderator />
    </template>
  </LLPopper>
</template>

<script>
import LLPopper from '@/components/utils/LLPopper.vue'
import MemberProfileCard from '@/components/pages/forum/member-profile/MemberProfileCard.vue'

export default {
  name: 'ForumResponseBodyTextOutputMention',
  components: { LLPopper, MemberProfileCard },
  props: {
    prospectId: { type: String, default: null },
    userId: { type: String, default: null },
    fullName: { type: String, default: null },
    basic: { type: Boolean, default: false },
    isAnonymous: { type: Boolean, default: false },
    anonymousId: { type: String, default: null },
    toModerator: { type: Boolean, default: false },
    id: { type: String, default: null },
    isReply: { type: Boolean, default: false },
    chatId: { type: [Number, String], default: null }
  },
  computed: {
    // Anonymous participants have no fetchable profile: the id alone renders the static card.
    hasAnonymousTarget() {
      return !!this.anonymousId && !this.toModerator
    },
    // Chat-scoped member profile is only available for a concrete member of this chat.
    hasMemberTarget() {
      return !!this.prospectId && !!this.chatId
    },
    // A mention of a concrete staff user: the API resolves the id to a reduced member profile.
    hasUserTarget() {
      return !this.isAnonymous && !this.prospectId && !!this.userId && !!this.chatId
    },
    // The static moderator card is only for mentions that carry no concrete user (e.g. a hidden
    // moderator in an anonymous chat); a user mention without a chat to load stays plain.
    hasModeratorTarget() {
      return !this.prospectId && !this.userId && !!this.toModerator
    },
    // A mention we cannot resolve stays plain; anonymous ones keep the hidden identity.
    isPlain() {
      return this.basic || (!this.hasAnonymousTarget && !this.hasMemberTarget && !this.hasModeratorTarget)
    },
    classList() {
      return {
        [`${this.$options.name}_is-anonymous`]: this.isAnonymous,
        [`${this.$options.name}_interactive`]: !this.isPlain
      }
    },
    text() {
      const moderator = this.$t('forum.response.moderator_reply_user_name')
      const anonymous = this.$t('forum.response.anonymous_reply_user_name')
      const anonymousWithId = this.$t('forum.response.anonymous_reply_user_name_with_id', {
        id: this.anonymousId
      })
      const moderatorWithId = this.$t('forum.response.moderator_reply_user_name_with_id', {
        id: this.anonymousId
      })

      if (this.isAnonymous && this.toModerator) {
        if (this.anonymousId) {
          return `@${moderatorWithId}`
        }
        return `@${moderator}`
      } else if (this.isAnonymous) {
        if (this.anonymousId) {
          return `@${anonymousWithId}`
        }
        return `@${anonymous}`
      }
      if (!this.fullName) return this.$t('not-available')
      return `@${this.fullName}`
    }
  }
}
</script>

<style scoped lang="scss">
.ForumResponseBodyTextOutputMention {
  @apply inline select-none;
  @apply text-accent-01;

  ::v-deep .LLPopper__trigger {
    @apply inline;
  }

  &_interactive {
    @apply cursor-pointer;
  }

  &_is-anonymous {
    @apply text-neutral-01-400;
  }
}
</style>
